import React, { useEffect, useMemo, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@vehix/shared/admin/portal-bridge';
import { useTheme } from '@vehix/portal/hooks/useTheme';
import { SystemRole } from '@vehix/portal/types';
import { Tabs, TabsList, TabsTrigger } from '@vehix/portal/components/ui/tabs';
import { Loader2, BookOpen, Server, Layers, Library, type LucideIcon } from 'lucide-react';

const RedocStandalone = React.lazy(() =>
  import('redoc').then(module => ({ default: module.RedocStandalone }))
);

type SpecKey = 'backend' | 'frontend' | 'combined';

interface VehixPalette {
  background: string;
  surface: string;
  surfaceMuted: string;
  border: string;
  text: string;
  textMuted: string;
  primary: string;
  primaryContrast: string;
  accent: string;
  navBackground: string;
  navText: string;
  navHover: string;
  success: string;
  warning: string;
  danger: string;
}

const VEHIX_LIGHT: VehixPalette = {
  background: '#faf8f5',
  surface: '#ffffff',
  surfaceMuted: '#f5f3f0',
  border: '#dcd5cb',
  text: '#1c1917',
  textMuted: '#78716c',
  primary: '#d4854a',
  primaryContrast: '#ffffff',
  accent: '#0ea5e9',
  navBackground: '#221f1d',
  navText: '#d9d6d3',
  navHover: '#2d2925',
  success: '#16a34a',
  warning: '#d97706',
  danger: '#dc2626'
};

const VEHIX_DARK: VehixPalette = {
  background: '#1a1714',
  surface: '#23201d',
  surfaceMuted: '#2d2925',
  border: '#3d3833',
  text: '#f3f0eb',
  textMuted: '#b8b1a7',
  primary: '#e2a070',
  primaryContrast: '#1a1714',
  accent: '#38bdf8',
  navBackground: '#15120f',
  navText: '#d9d6d3',
  navHover: '#2d2925',
  success: '#22c55e',
  warning: '#f59e0b',
  danger: '#f87171'
};

const FONT_STACK =
  'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
const MONO_STACK =
  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Monaco, Consolas, "Liberation Mono", monospace';

function buildRedocTheme(p: VehixPalette) {
  return {
    spacing: { unit: 5, sectionHorizontal: 40, sectionVertical: 40 },
    breakpoints: { small: '50rem', medium: '85rem', large: '105rem' },
    colors: {
      tonalOffset: 0.2,
      primary: { main: p.primary, contrastText: p.primaryContrast },
      success: { main: p.success },
      warning: { main: p.warning },
      error: { main: p.danger },
      text: { primary: p.text, secondary: p.textMuted },
      border: { dark: p.border, light: p.border },
      responses: {
        success: { color: p.success, backgroundColor: p.success + '14' },
        error: { color: p.danger, backgroundColor: p.danger + '14' },
        redirect: { color: p.warning, backgroundColor: p.warning + '14' },
        info: { color: p.accent, backgroundColor: p.accent + '14' }
      },
      http: {
        get: p.accent,
        post: p.success,
        put: p.warning,
        options: p.textMuted,
        patch: p.warning,
        delete: p.danger,
        basic: p.textMuted,
        link: p.accent,
        head: p.textMuted
      }
    },
    schema: {
      linesColor: p.border,
      defaultDetailsWidth: '75%',
      typeNameColor: p.accent,
      typeTitleColor: p.text,
      requireLabelColor: p.danger,
      labelsTextSize: '0.85em',
      nestingSpacing: '1em',
      nestedBackground: p.surfaceMuted,
      arrow: { size: '1.1em', color: p.textMuted }
    },
    typography: {
      fontSize: '15px',
      lineHeight: '1.6em',
      fontWeightRegular: '400',
      fontWeightBold: '600',
      fontWeightLight: '300',
      fontFamily: FONT_STACK,
      smoothing: 'antialiased',
      optimizeSpeed: true,
      headings: {
        fontFamily: FONT_STACK,
        fontWeight: '600',
        lineHeight: '1.4em'
      },
      code: {
        fontSize: '13px',
        fontFamily: MONO_STACK,
        lineHeight: '1.5em',
        fontWeight: '400',
        color: p.primary,
        backgroundColor: p.surfaceMuted,
        wrap: true
      },
      links: {
        color: p.primary,
        visited: p.primary,
        hover: p.accent,
        textDecoration: 'none',
        hoverTextDecoration: 'underline'
      }
    },
    sidebar: {
      width: '280px',
      backgroundColor: p.navBackground,
      textColor: p.navText,
      activeTextColor: p.primary,
      groupItems: {
        activeBackgroundColor: p.navHover,
        activeTextColor: p.primary,
        textTransform: 'uppercase'
      },
      level1Items: {
        activeBackgroundColor: p.navHover,
        activeTextColor: p.primary,
        textTransform: 'none'
      },
      arrow: { size: '1.5em', color: p.navText }
    },
    logo: { gutter: '20px' },
    rightPanel: {
      backgroundColor: '#15120f',
      width: '40%',
      textColor: '#f3f0eb'
    },
    codeBlock: {
      backgroundColor: '#0f0d0b',
      tokens: {}
    },
    fab: { backgroundColor: p.primary, color: p.primaryContrast }
  };
}

const SPEC_TABS: ReadonlyArray<{
  value: SpecKey;
  label: string;
  description: string;
  Icon: LucideIcon;
}> = [
  {
    value: 'backend',
    label: 'Backend & Supabase',
    description: 'REST endpoints, RPC functions, and Supabase resources.',
    Icon: Server
  },
  {
    value: 'frontend',
    label: 'Frontend Services',
    description:
      'TypeScript/JavaScript service abstractions that orchestrate API calls, transform data, and coordinate caching.',
    Icon: Layers
  },
  {
    value: 'combined',
    label: 'Unified Reference',
    description: 'All backend and frontend specs merged into a single reference document.',
    Icon: Library
  }
];

export const ApiDocsPage: React.FC = () => {
  const { user, userProfile, loading: authLoading } = useAuth();
  const { resolvedTheme } = useTheme();
  const location = useLocation();
  const [selectedSpec, setSelectedSpec] = useState<SpecKey>('backend');
  const [mergedSpec, setMergedSpec] = useState<unknown>(undefined);
  const [specLoading, setSpecLoading] = useState(false);
  const [specError, setSpecError] = useState<string | undefined>(undefined);

  const isSuperAdmin = userProfile?.roles?.some(role => role.name === SystemRole.SUPER_ADMIN);

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'API Documentation - Vehix';
    return () => {
      document.title = previousTitle;
    };
  }, []);

  useEffect(() => {
    if (selectedSpec !== 'combined') return;
    let cancelled = false;
    const load = async () => {
      setSpecLoading(true);
      setSpecError(undefined);
      try {
        const [backendRes, frontendRes] = await Promise.all([
          fetch('/docs/api/openapi.yaml'),
          fetch('/docs/api/openapi-frontend-services.yaml')
        ]);
        const [backendText, frontendText] = await Promise.all([
          backendRes.text(),
          frontendRes.text()
        ]);
        const jsyaml = await import('js-yaml');
        const backendSpec = jsyaml.load(backendText) as any;
        const frontendSpec = jsyaml.load(frontendText) as any;
        const merged = {
          ...backendSpec,
          info: {
            ...backendSpec.info,
            title: 'Vehix Platform — Unified API Reference',
            description:
              (backendSpec.info?.description ?? '') +
              '\n\n---\n\n' +
              (frontendSpec.info?.description ?? '')
          },
          tags: [...(backendSpec.tags ?? []), ...(frontendSpec.tags ?? [])],
          paths: { ...(backendSpec.paths ?? {}), ...(frontendSpec.paths ?? {}) },
          components: {
            schemas: {
              ...(backendSpec.components?.schemas ?? {}),
              ...(frontendSpec.components?.schemas ?? {})
            },
            responses: {
              ...(backendSpec.components?.responses ?? {}),
              ...(frontendSpec.components?.responses ?? {})
            },
            securitySchemes: {
              ...(backendSpec.components?.securitySchemes ?? {}),
              ...(frontendSpec.components?.securitySchemes ?? {})
            }
          }
        };
        if (!cancelled) setMergedSpec(merged);
      } catch (err) {
        if (!cancelled) {
          setSpecError(err instanceof Error ? err.message : 'Failed to load merged spec');
        }
      } finally {
        if (!cancelled) setSpecLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [selectedSpec]);

  const palette = resolvedTheme === 'dark' ? VEHIX_DARK : VEHIX_LIGHT;
  const redocTheme = useMemo(() => buildRedocTheme(palette), [palette]);

  const activeTab = SPEC_TABS.find(t => t.value === selectedSpec) ?? SPEC_TABS[0]!;

  if (authLoading) {
    return (
      <div className='flex items-center justify-center min-h-screen bg-background'>
        <Loader2 className='w-6 h-6 animate-spin text-primary' />
        <span className='ml-3 text-sm text-muted-foreground'>Checking authorization…</span>
      </div>
    );
  }

  if (!user) {
    return <Navigate to='/auth/sign-in' state={{ from: location }} replace />;
  }

  if (!isSuperAdmin) {
    return (
      <div className='flex flex-col items-center justify-center min-h-screen p-6 bg-background'>
        <div className='text-center max-w-md'>
          <h1 className='text-2xl font-semibold text-foreground mb-2'>Access Denied</h1>
          <p className='text-sm text-muted-foreground mb-6'>
            You need super admin privileges to view the API documentation.
          </p>
          <button
            onClick={() => window.history.back()}
            className='px-4 py-2 rounded-md bg-primary text-primary-foreground text-sm font-medium
              hover:bg-primary/90 transition-colors'
          >
            Go back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className='flex flex-col h-full bg-background'>
      <header className='border-b border-border bg-card'>
        <div className='px-6 pt-6 pb-2'>
          <div className='flex items-start justify-between gap-6 flex-wrap'>
            <div className='flex items-start gap-3 min-w-0'>
              <div
                className='shrink-0 mt-1 w-9 h-9 rounded-md bg-primary/10 text-primary
                  flex items-center justify-center'
              >
                <BookOpen className='w-5 h-5' aria-hidden='true' />
              </div>
              <div className='min-w-0'>
                <h1 className='text-xl font-semibold text-foreground tracking-tight'>
                  API Reference
                </h1>
                <p className='text-sm text-muted-foreground mt-0.5 max-w-2xl'>
                  {activeTab.description}
                </p>
              </div>
            </div>
            <div
              className='text-xs text-muted-foreground bg-muted/50 border border-border
                rounded-full px-3 py-1 shrink-0'
            >
              {userProfile?.email} · Super Admin
            </div>
          </div>
        </div>
        <div className='px-6 pb-4 pt-3'>
          <Tabs
            value={selectedSpec}
            onValueChange={value => setSelectedSpec(value as SpecKey)}
          >
            <TabsList className='bg-muted/60'>
              {SPEC_TABS.map(({ value, label, Icon }) => (
                <TabsTrigger
                  key={value}
                  value={value}
                  className='data-[state=active]:bg-background data-[state=active]:text-primary
                    data-[state=active]:shadow-sm flex items-center gap-2'
                >
                  <Icon className='w-4 h-4' />
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>
        </div>
      </header>

      <div className='flex-1 min-h-0 overflow-hidden'>
        <React.Suspense
          fallback={
            <div className='flex items-center justify-center h-96'>
              <Loader2 className='w-6 h-6 animate-spin text-primary' />
              <span className='ml-3 text-sm text-muted-foreground'>Loading documentation…</span>
            </div>
          }
        >
          {specLoading ? (
            <div className='flex items-center justify-center h-96'>
              <Loader2 className='w-6 h-6 animate-spin text-primary' />
              <span className='ml-3 text-sm text-muted-foreground'>
                Merging API specifications…
              </span>
            </div>
          ) : specError ? (
            <div className='flex flex-col items-center justify-center h-96 px-6 text-center'>
              <p className='text-sm font-medium text-destructive mb-1'>
                Couldn't load the unified spec.
              </p>
              <p className='text-xs text-muted-foreground'>{specError}</p>
            </div>
          ) : (
            <RedocKey
              specKey={selectedSpec}
              theme={redocTheme}
              {...(selectedSpec === 'backend' && { specUrl: '/docs/api/openapi.yaml' })}
              {...(selectedSpec === 'frontend' && {
                specUrl: '/docs/api/openapi-frontend-services.yaml'
              })}
              {...(selectedSpec === 'combined' && mergedSpec ? { spec: mergedSpec } : {})}
            />
          )}
        </React.Suspense>
      </div>
    </div>
  );
};

interface RedocKeyProps {
  specKey: SpecKey;
  specUrl?: string;
  spec?: unknown;
  theme: ReturnType<typeof buildRedocTheme>;
}

const RedocKey: React.FC<RedocKeyProps> = ({ specKey, specUrl, spec, theme }) => {
  return (
    <div className='h-full overflow-hidden' key={`${specKey}-${(theme.colors as any).text.primary}`}>
      <RedocStandalone
        {...(specUrl ? { specUrl } : {})}
        {...(spec ? { spec: spec as any } : {})}
        options={{
          theme: theme as any,
          hideDownloadButton: false,
          hideHostname: false,
          disableSearch: false,
          expandResponses: '200,201',
          expandSingleSchemaField: true,
          jsonSampleExpandLevel: 2,
          menuToggle: true,
          nativeScrollbars: false,
          requiredPropsFirst: true,
          scrollYOffset: 0,
          showExtensions: true,
          sortPropsAlphabetically: false,
          pathInMiddlePanel: false,
          payloadSampleIdx: 0,
          hideLoading: false
        }}
      />
    </div>
  );
};
